<template>
    <div>
        <card-header title="New Plan Offer" icon="fa-envelope-open-dollar" />
        <card-toolbar>
            <button><i class="far fa-plus"></i> New Offer</button>
        </card-toolbar>
        <card-body v-if="plan">
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Offer" />

                <container-list-item padded no-border v-for="offer in offers">
                    <div class="columns">
                        <div class="column">
                            <b-field label="Adjustment By">
                                <b-select v-model="offer.type">
                                    <option value="monthly">Monthly Installment</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Monthly">
                                <b-input v-model="offer.monthly" :lazy="true" @input="calcOfferByMonthly(offer)"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Agreement Fee">
                                <b-input v-model="offer.fees.agreementFee" @input="calcOfferByMonthly(offer)"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Term">
                                <b-slider v-model="offer.term" :min="plan.term" :max="100" @input="calcOfferByTerm(offer)"></b-slider>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Deferment" label-position="inside">
                                <b-datepicker
                                    position="is-bottom-right"
                                    append-to-body
                                    v-model="offer.defer_on"
                                    :date-formatter='(date) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC" }).format(date)'
                                    @change="calcOfferByTerm(offer)"
                                ></b-datepicker>
                            </b-field>
                        </div>
                    </div>

                    <p>Fees: {{ offer.fees }}</p>
                    <p>Fee Total: {{ offer.new.fees | currency }}</p>
                    <p>New Total: {{ offer.new.total | currency }}</p>
                    <p>New Monthly: {{ offer.new.monthly | currency }}</p>
                    <p>New Term: {{ offer.new.term }}</p>
                    <p>Implied Interest Rate: {{ (offer.impliedInterestRate * 100).toFixed(2) }}%</p>

                </container-list-item>

                <subheader-list-item title="Current Plan Calculations" icon="fa-calculator" />

                <data-row title="Defer Days">
                    <div class="is-size-7 has-text-grey">Number of days payment was deferred since sign date.</div>
                    {{ calculated.deferDays }}
                </data-row>
                <data-row title="Term Days">
                    <div class="is-size-7 has-text-grey">Number of days in the full term of agreement.</div>
                    {{ calculated.termDays }}
                </data-row>
                <data-row title="Active Days">
                    <div class="is-size-7 has-text-grey">Number of days into active plan, since deferment.</div>
                    {{ calculated.activeDays }}
                </data-row>
                <data-row title="Defer Interest">
                    <div class="is-size-7 has-text-grey">Interest of deferment period.</div>
                    {{ calculated.deferInterest | currency }}
                </data-row>
                <data-row title="Term Interest">
                    <div class="is-size-7 has-text-grey">Interest of full agreement term.</div>
                    {{ calculated.termInterest | currency }}
                </data-row>
                <data-row title="Active Interest">
                    <div class="is-size-7 has-text-grey">Interest of active plan days.</div>
                    {{ calculated.activeInterest | currency }}
                </data-row>
                <data-row title="Full Interest">
                    <div class="is-size-7 has-text-grey">Total Interest per agreement (Defer Interest + Term Interest).</div>
                    {{ calculated.fullInterest | currency }}
                </data-row>
                <data-row title="Payoff Interest">
                    <div class="is-size-7 has-text-grey">Interest if doing full payoff (Defer Interest + Active Interest)</div>
                    {{ calculated.payoffInterest | currency }}
                </data-row>
                <data-row title="Payoff Amount">
                    <div class="is-size-7 has-text-grey">Total Amount due if doing a full payoff.</div>
                    {{ calculated.payoffAmount | currency }}
                </data-row>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                    ><i class="fas fa-plus mr-2"></i>Submit Offer
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardFooter from "@/TIER/components/CardFooter";

    import {client as http} from "@/http_client";
    import PlanTransactionForm from "./PlanTransactionForm";
    import moment from "moment";
    import {mapGetters} from "vuex";
    import CardList from "../../TIER/components/CardList.vue";
    import DataRow from "../../TIER/components/DataRow.vue";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem.vue";
    import async from "async";
    import ContainerListItem from "../../TIER/components/ContainerListItem.vue";
    import numeral from "numeral";

    const financial = require("financial");

    export default {
        components: {ContainerListItem, SubheaderListItem, DataRow, CardList, PlanTransactionForm, CardHeader, CardToolbar, CardBody, CardFooter},
        props: ['card', 'props'],
        data() {
            return {
                plan: null,
                offers: [],
            };
        },
        computed: {
            calculated: function () {
                let deferMonths = Math.max(0, moment(this.plan.defer_on).diff(moment(this.plan.created_at), 'months'));
                let deferDays = Math.max(0, moment(this.plan.defer_on).diff(moment(this.plan.created_at), 'days'));
                let termDays = Math.max(0, moment(this.plan.defer_on).add(this.plan.term - 1, 'months').diff(moment(this.plan.defer_on), 'days'));
                let activeDays = Math.max(0, moment().diff(moment(this.plan.defer_on), 'days'));
                let deferInterest = (deferDays) * 0.0005 * this.plan.agreement_total;
                let termInterest = (termDays) * 0.0005 * this.plan.agreement_total;
                let activeInterest = (activeDays) * 0.0005 * this.plan.agreement_total;
                let fullInterest = (deferDays + termDays) * 0.0005 * this.plan.agreement_total;
                let payoffInterest = (deferDays + activeDays) * 0.0005 * this.plan.agreement_total;
                let payoffAmount = this.plan.agreement_total - this.plan.paid + ((deferDays + activeDays) * 0.0005 * this.plan.agreement_total);

                return {
                    deferDays,
                    termDays,
                    activeDays,
                    deferInterest,
                    termInterest,
                    activeInterest,
                    fullInterest,
                    payoffInterest,
                    payoffAmount
                };
            }
        },
        methods: {
            loadPlan: function () {
                this.$emit('loading', true);

                async.series([
                    (cb) => {
                        http.get('payment-plans/' + this.props.planId, {force: true}).then(response => {
                            this.plan = response.data;
                            this.plan.defer_on = moment.utc(response.data.defer_on).toDate();
                            this.plan.last_paid_month_on = moment.utc(this.plan.last_paid_month_on);
                            this.plan.term = parseInt(this.plan.term);
                            this.plan.monthly = parseFloat(this.plan.monthly);
                            this.plan.total = parseFloat(this.plan.total);

                            this.offers.push({
                                type: 'monthly',
                                term: this.plan.term,
                                monthly: this.plan.monthly,
                                defer_on: this.plan.defer_on,
                                fees: {
                                    agreementFee: 50,
                                    termPenalty: 0,
                                    paymentPenalty: 0,
                                    defermentFee: 0,
                                },
                                new: {
                                    total: 0,
                                    monthly: 0,
                                    term: 0,
                                    fees: 0,
                                },
                                impliedInterestRate: null
                            });

                            cb();

                        }).catch(err => {
                            cb(err);
                        });
                    }
                ], err => {
                    this.$emit('loading', false);
                });
            },
            calcOfferByMonthly(offer) {
                offer.new.monthly = parseFloat(offer.monthly);

                offer.new.fees = this.calculateAdditionalCosts(offer);
                offer.new.total = this.plan.total + offer.new.fees;
                offer.new.term = Math.ceil(offer.new.total / offer.new.monthly);


                console.log('calcOfferByMonthly', offer.new.total, offer.new.monthly, offer.new.term);

                offer.impliedInterestRate = this.calculateImpliedInterestRate(this.plan.total, offer.new.monthly, offer.new.term);

                const P_orig = this.plan.total;
                const I_orig = this.plan.monthly;
                const I_new = offer.new.monthly;
                const r_orig = 0; // 5% annual interest
                const penaltyMultiplier = 1.3; // 10% penalty rate

                console.log(P_orig, I_orig, I_new, r_orig, penaltyMultiplier);

                try {
                    const result = this.calculateAdjustedPlanFlexibleTerm(P_orig, I_orig, I_new, r_orig, penaltyMultiplier);

                    console.log("----- Payment Plan Adjustment -----");
                    console.log(`Original Principal: $${P_orig.toFixed(2)}`);
                    console.log(`Original Monthly Installment: $${I_orig.toFixed(2)}`);
                    console.log(`New Monthly Installment: $${I_new.toFixed(2)}`);
                    console.log(`Adjusted Principal: $${result.adjustedPrincipal.toFixed(2)}`);
                    console.log(`Adjusted Term: ${result.adjustedTermMonths} months`);
                    console.log(`Implied Annual Interest Rate: ${(result.impliedInterestRate * 100).toFixed(2)}%`);
                    console.log("-----------------------------------");
                } catch (error) {
                    console.error("Error:", error.message);
                }

            },
            calculateAdjustedPlanFlexibleTerm(P_orig, I_orig, I_new, r_orig, penaltyMultiplier) {
                // Step 1: Calculate deviation
                const deviation = (I_orig - I_new) / I_orig;

                // Validation: Ensure that the new installment is less than or equal to the original
                if (I_new > I_orig) {
                    throw new Error("New installment cannot be greater than the original installment.");
                }

                // Step 2: Calculate the penalty based on deviation
                const penalty = P_orig * deviation * penaltyMultiplier;

                // Step 3: Calculate the adjusted principal
                const P_new = P_orig + penalty;

                // Step 4: Calculate the original number of periods (months)
                const n_orig = financial.nper(r_orig / 12, I_orig, -P_orig);

                // Step 5: Calculate the adjusted term (n_new) based on the new installment and adjusted principal
                const n_new = financial.nper(r_orig / 12, I_new, -P_new);

                // Validation: Ensure that the new term is a valid number
                if (isNaN(n_new) || n_new <= 0) {
                    throw new Error("The new installment is too low to cover the adjusted principal with the given interest rate.");
                }

                // Convert n_new to an integer (ceil to ensure full payment)
                const n_new_ceil = Math.ceil(n_new);

                return {
                    adjustedPrincipal: P_new,
                    adjustedTermMonths: n_new_ceil,
                    impliedInterestRate: r_orig // Interest rate remains unchanged
                };
            },
            calcOfferByTerm(offer) {

                // offer.feesTotal = this.calculateAdditionalCosts(offer);
                // offer.newTotal = this.plan.total + offer.feesTotal;
                //
                // // Calculate implied interest rate
                // offer.impliedInterestRate = this.calculateImpliedInterestRate(this.plan.total, offer.newTotal, offer.term);
            },

            calculateAdditionalCosts(offer) {
                const originalTerm = this.plan.term;
                const originalMonthlyPayment = this.plan.monthly;
                const penaltyRatePerMonth = 50;
                const termPenaltyRate = 0.05;
                const monthlyPenaltyRate = 1.3;
                const defermentRate = 20;

                // if (offer.new.term > originalTerm) {
                //     const termDifference = offer.new.term - originalTerm;
                //     offer.fees.termPenalty = ((offer.new.term - originalTerm) * penaltyRatePerMonth) * (1 + (termDifference * termPenaltyRate));
                // }

                if (offer.new.monthly < originalMonthlyPayment) {
                    let paymentDifference = originalMonthlyPayment - offer.new.monthly;
                    console.log(originalMonthlyPayment, '-', offer.new.monthly);
                    console.log(paymentDifference, (monthlyPenaltyRate * paymentDifference / originalMonthlyPayment))
                    offer.fees.paymentPenalty = this.plan.total * ((monthlyPenaltyRate * paymentDifference) / originalMonthlyPayment);
                }

                // let deferMonths = Math.max(0, moment(this.plan.defer_on).diff(moment(offer.defer_on), 'months'));
                // offer.fees.defermentFee = deferMonths * defermentRate;

                return Object.values(offer.fees).reduce((acc, fee) => acc + (Number.isInteger(fee) ? fee : parseInt(fee, 10)), 0);
            },

            calculateImpliedInterestRate(originalPrincipal, monthlyPayment, months) {
                // Use the financial.rate() method
                // rate(periods, payment, presentValue, futureValue, type)
                // In this case, futureValue is 0 and type is 0 (end of the period)

                const monthlyInterestRate = financial.rate(months, -monthlyPayment, originalPrincipal, 0);

                // Convert the monthly interest rate to an annual interest rate
                const annualInterestRate = monthlyInterestRate * 12;

                return annualInterestRate;
            },

            calculatePayment(principal, monthlyRate, term) {
                return (
                    (principal * monthlyRate * Math.pow(1 + monthlyRate, term)) /
                    (Math.pow(1 + monthlyRate, term) - 1)
                );
            },

            // Method to push new offers
            addOffer() {
                this.offers.push({
                    term: this.plan.term,
                    monthly: this.plan.monthly,
                    defer_on: this.plan.defer_on,
                    newTotal: null,
                    impliedInterestRate: null,
                });
            },
            submit: function () {
                this.$emit('loading', true);
            }
        },
        mounted() {
            this.loadPlan();
        }
    };
</script>
